import React, { useState, useRef, useEffect } from 'react';
import Icons from '../../assets/index';

interface IDropdownOption {
  label: string;
  value: string;
}

interface IDynamicDropdownProps {
  options: IDropdownOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  isFullWidth?: boolean;
  isDisabled?: boolean;
}

const Dropdown: React.FC<IDynamicDropdownProps> = ({
  options,
  selectedValue,
  onChange,
  placeholder = 'Select',
  error = false,
  errorMessage = 'This field is required.',
  isFullWidth = false,
  isDisabled,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = options.find((opt) => opt.value === selectedValue)?.label || '';

  const calculateDropdownDirection = () => {
    if (inputRef.current && dropdownRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      const dropdownHeight = 240; // Approximate max height (60px * 4 items)

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownDirection('up');
      } else {
        setDropdownDirection('down');
      }
    }
  };

  const toggleDropdown = () => {
    if (!showOptions) {
      calculateDropdownDirection();
    }
    setShowOptions(!showOptions);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${isFullWidth ? 'w-full' : 'w-[350px]'}`} ref={dropdownRef}>
      <input
        ref={inputRef}
        readOnly
        value={selectedLabel}
        onClick={toggleDropdown}
        placeholder={placeholder}
        disabled={isDisabled}
        className={`h-[40px] w-full border-2 px-2 pr-10 rounded-md text-ds-gray-800 cursor-pointer outline-none text-xs submission ${
          error
            ? 'border-red-600'
            : showOptions
              ? 'border-ds-mc-blue-400'
              : 'border-ds-gray-100 mb-1'
        }`}
      />

      {/* Dropdown icon */}
      <div
        className={`absolute ${error ? 'top-5' : 'top-1/2'} right-3 transform -translate-y-1/2 pointer-events-none text-gray-400`}
      >
        <Icons.arrowDownIcon />
      </div>

      {showOptions && (
        <div
          className={`absolute z-50 w-full max-h-60 overflow-auto bg-white rounded-md shadow-xl px-2 ${
            dropdownDirection === 'down' ? 'mt-2 top-full' : 'mb-2 bottom-full'
          }`}
        >
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setShowOptions(false);
              }}
              className="px-4 py-2 cursor-pointer whitespace-nowrap text-ds-gray-700 text-sm hover:bg-ds-mc-blue-100 border-b border-ds-cool-gray-1000"
            >
              {option.label}
            </div>
          ))}
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{errorMessage}</p>}
    </div>
  );
};

export default Dropdown;
